import { Link } from "react-router-dom";
import { ProgramWatermark } from "../../components/ProgramWatermark";
import { ScrollReveal } from "../../components/ScrollReveal";
import { Seo } from "../../components/Seo";
import { useLanguage } from "../../i18n/LanguageProvider";

const ICON_UNDERSTAND =
  "M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm5-2 4.5 4.5M8.5 11h5M11 8.5v5";
const ICON_PROTECT =
  "M12 3 5 6v5c0 4.4 3 8.3 7 9.5 4-1.2 7-5.1 7-9.5V6l-7-3Zm-3 9 2 2 4-4";
const ICON_BUILD = "M3 12h4l2.5-6 5 12 2.5-6H21";
const ICON_GROW = "M4 18l6-6 4 4 6-6M14 10h6v6";

const LineIcon = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
    <path
      d={d}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Home page.
 *
 * Deliberately short: hero, mission, failure education with the framework,
 * then programs and ways in. The fuller story lives on the About page.
 * Every claim comes from the revised content document; nothing about
 * pricing, credentials, clients or partners is asserted because the source
 * says nothing about them.
 */
export const HomePage = () => {
  const { t, path } = useLanguage();
  const pillars = [
    { key: "understand", icon: ICON_UNDERSTAND },
    { key: "protect", icon: ICON_PROTECT },
    { key: "build", icon: ICON_BUILD },
    { key: "grow", icon: ICON_GROW },
  ] as const;
  const serviceKeys = [
    "students",
    "parents",
    "professionals",
    "group",
    "individual",
  ] as const;
  const stepKeys = ["recognize", "reframe", "rebuild", "reengage"] as const;
  const partnerKeys = [
    "universities",
    "schools",
    "organizations",
    "researchers",
  ] as const;

  return (
    <>
      <Seo titleKey="meta.home.title" descriptionKey="meta.home.description" />

      {/* ------------------------------------------------------- hero */}
      <section className="gw-hero">
        <div className="gw-container gw-hero__inner">
          <ScrollReveal className="gw-hero__copy">
            <h1>
              <span className="gw-hero__line">{t("home.heroTitleLine1")}</span>{" "}
              <span className="gw-hero__line">{t("home.heroTitleLine2")}</span>
            </h1>
            <p className="gw-subtitle">{t("home.heroBody")}</p>
            <div className="gw-hero__actions">
              <Link to={path("/contact")} className="gw-btn gw-btn--on-dark">
                {t("home.ctaButton")}
              </Link>
              <Link
                to={path("/services")}
                className="gw-btn gw-btn--secondary gw-btn--outline-light"
              >
                {t("home.servicesLink")}
              </Link>
            </div>
          </ScrollReveal>

          <div className="gw-hero__figure" aria-hidden="true">
            <img
              src="/hero-people.webp"
              alt=""
              width={1200}
              height={789}
              decoding="async"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      {/* -------------------------------------------------- pillars */}
      <section className="gw-delivery gw-delivery--pillars">
        <div className="gw-container gw-delivery__inner">
          <h2 className="gw-visually-hidden">{t("home.pillarsHeading")}</h2>
          <div className="gw-delivery__grid">
            {pillars.map(({ key, icon }, index) => (
              <ScrollReveal key={key} delay={index * 90}>
                <article className="gw-delivery__item">
                  <span className="gw-delivery__icon">
                    <LineIcon d={icon} />
                  </span>
                  <div>
                    <h3 className="gw-delivery__label">
                      {t(`home.pillars.${key}.title`)}
                    </h3>
                    <p className="gw-delivery__body">
                      {t(`home.pillars.${key}.body`)}
                    </p>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------- mission */}
      <section className="gw-section">
        <div className="gw-container">
          <div className="gw-grid gw-grid--2" style={{ alignItems: "center" }}>
            <ScrollReveal direction="left">
              <p className="gw-eyebrow">{t("home.missionEyebrow")}</p>
              <h2 className="gw-title gw-gradient-text">
                {t("home.missionTitle")}
              </h2>
              <p className="gw-lead">{t("home.missionBody")}</p>
              <p style={{ marginTop: "1.75rem" }}>
                <Link to={path("/about")} className="gw-btn gw-btn--primary">
                  {t("home.missionLink")}
                </Link>
              </p>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={120}>
              <img
                className="gw-mission-image"
                src="/mission-climbing.webp"
                alt={t("home.missionImageAlt")}
                width={1000}
                height={1000}
                loading="lazy"
                decoding="async"
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ---------------------------------- failure education + framework */}
      <section className="gw-section gw-section--ink">
        <div className="gw-container">
          <ScrollReveal>
            <p className="gw-eyebrow">{t("home.focusEyebrow")}</p>
            <h2 className="gw-title">{t("home.focusTitle")}</h2>
            <p className="gw-subtitle">{t("home.focusBody")}</p>
          </ScrollReveal>

          <ScrollReveal delay={80}>
            <h3 className="gw-framework-heading">
              {t("home.frameworkHeading")}
            </h3>
          </ScrollReveal>

          <div className="gw-steps gw-steps--row" style={{ marginTop: "2rem" }}>
            {stepKeys.map((key, index) => (
              <ScrollReveal key={key} delay={index * 120} className="gw-step">
                <h4 className="gw-step__title">
                  {t(`model.steps.${key}.title`)}
                </h4>
                <p style={{ color: "rgb(255 255 255 / 75%)" }}>
                  {t(`model.steps.${key}.summary`)}
                </p>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={200}>
            <p style={{ marginTop: "2.5rem" }}>
              <Link
                to={`${path("/about")}#framework`}
                className="gw-btn gw-btn--on-dark"
              >
                {t("home.modelLink")}
              </Link>
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* --------------------------------------------------- services */}
      <section className="gw-section gw-section--sunken">
        <div className="gw-container">
          <ScrollReveal>
            <p className="gw-eyebrow">{t("home.servicesEyebrow")}</p>
            <h2 className="gw-title">{t("services.title")}</h2>
            <p className="gw-subtitle">{t("home.servicesSubtitle")}</p>
          </ScrollReveal>

          <div className="gw-grid gw-grid--3" style={{ marginTop: "2.5rem" }}>
            {serviceKeys.map((key, index) => (
              <ScrollReveal key={key} delay={index * 80}>
                <article
                  className="gw-card gw-card--interactive gw-card--watermarked"
                  style={{ height: "100%" }}
                >
                  <ProgramWatermark program={key} />
                  <p className="gw-card__index">
                    {t(`services.items.${key}.index`)}
                  </p>
                  <h3
                    style={{
                      fontSize: "var(--gw-text-lg)",
                      marginBlock: "0.4rem 0.75rem",
                    }}
                  >
                    {t(`services.items.${key}.title`)}
                  </h3>
                  <p style={{ color: "var(--gw-ink-muted)" }}>
                    {t(`services.items.${key}.summary`)}
                  </p>
                </article>
              </ScrollReveal>
            ))}

            <ScrollReveal delay={serviceKeys.length * 80}>
              <article
                className="gw-card"
                style={{
                  height: "100%",
                  background: "var(--gw-sky-100)",
                  borderColor: "transparent",
                }}
              >
                <h3
                  style={{
                    fontSize: "var(--gw-text-lg)",
                    marginBlock: "1.6rem 0.75rem",
                  }}
                >
                  {t("home.servicesStartTitle")}
                </h3>
                <p style={{ color: "var(--gw-ink-muted)" }}>
                  {t("home.servicesStartBody")}
                </p>
                <p style={{ marginTop: "1.25rem" }}>
                  <Link
                    to={path("/contact")}
                    className="gw-btn gw-btn--primary gw-btn--sm"
                  >
                    {t("home.servicesStartLink")}
                  </Link>
                </p>
              </article>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={200}>
            <p style={{ marginTop: "2rem" }}>
              <Link to={path("/services")} className="gw-btn gw-btn--primary">
                {t("home.servicesLink")}
              </Link>
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ------------------------------------------------- check-in CTA */}
      <section className="gw-section">
        <div
          className="gw-container gw-container--narrow"
          style={{ textAlign: "center" }}
        >
          <ScrollReveal>
            <p className="gw-eyebrow" style={{ justifyContent: "center" }}>
              {t("home.assessmentEyebrow")}
            </p>
            <h2 className="gw-title">{t("assessment.title")}</h2>
            <p className="gw-subtitle" style={{ marginInline: "auto" }}>
              {t("assessment.subtitle")}
            </p>
            <p style={{ marginTop: "1.5rem" }}>
              <Link to={path("/check-in")} className="gw-btn gw-btn--primary">
                {t("home.assessmentLink")}
              </Link>
            </p>
            <p className="gw-note" style={{ marginTop: "1rem" }}>
              {t("assessment.privacyNotice")}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* --------------------------------------------------- partners */}
      <section className="gw-section gw-section--sunken">
        <div className="gw-container">
          <ScrollReveal>
            <p className="gw-eyebrow">{t("home.partnersEyebrow")}</p>
            <h2 className="gw-title">{t("partnerships.title")}</h2>
            <p className="gw-subtitle">{t("home.partnersSubtitle")}</p>
          </ScrollReveal>

          <div className="gw-grid gw-grid--4" style={{ marginTop: "2.5rem" }}>
            {partnerKeys.map((key, index) => (
              <ScrollReveal key={key} delay={index * 80}>
                <article className="gw-card" style={{ height: "100%" }}>
                  <h3
                    style={{
                      fontSize: "var(--gw-text-base)",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {t(`home.partners.${key}.title`)}
                  </h3>
                  <p
                    style={{
                      color: "var(--gw-ink-muted)",
                      fontSize: "var(--gw-text-sm)",
                    }}
                  >
                    {t(`home.partners.${key}.body`)}
                  </p>
                </article>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={200}>
            <div className="gw-hero__actions">
              <Link
                to={path("/partnerships")}
                className="gw-btn gw-btn--primary"
              >
                {t("home.partnersLink")}
              </Link>
              <Link to={path("/careers")} className="gw-btn gw-btn--secondary">
                {t("home.careersLink")}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* -------------------------------------------------- closing CTA */}
      <section className="gw-section gw-section--gradient">
        <div
          className="gw-container gw-container--narrow"
          style={{ textAlign: "center" }}
        >
          <ScrollReveal>
            <h2 className="gw-title">{t("home.ctaTitle")}</h2>
            <p className="gw-subtitle" style={{ marginInline: "auto" }}>
              {t("home.ctaBody")}
            </p>
            <div
              className="gw-hero__actions"
              style={{ justifyContent: "center", marginTop: "2rem" }}
            >
              <Link to={path("/contact")} className="gw-btn gw-btn--on-dark">
                {t("home.ctaButton")}
              </Link>
              <Link
                to={path("/sign-up")}
                className="gw-btn gw-btn--secondary"
                style={{ color: "#fff", borderColor: "rgb(255 255 255 / 45%)" }}
              >
                {t("home.ctaSecondary")}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
};
